import { Grid } from 'src/design/atoms/Grid';
import { Loader } from 'src/design/atoms/Loader';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { groupBy } from 'lodash';
import { MRT_ColumnDef } from 'mantine-react-table';
import React from 'react';
import { JobDto, PostDto } from 'src/api/FiretrolApiClient';
import { useAssignedPosts } from 'src/api/queries/posts/useAssignedPosts';
import { useJobs } from 'src/hooks/useJobs';
import { ViewActionIcon } from 'src/design/atoms/ViewActionIcon';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';
import { EditPost } from './components/EditPost';
import { PostItem } from './components/PostItem';

interface JobByUserActivityProps {
  jobId: number;
}

interface UserPost {
  name: string;
  id: string;
  posts: PostDto[];
}

export const JobByUserActivity: React.FC<JobByUserActivityProps> = ({
  jobId,
}) => {
  const { selectedJob } = useJobs();
  const [selectedUserId, setSelectedUserId] = React.useState<string | null>(
    null,
  );
  const [openPost, setOpenPost] = React.useState<PostDto | null>(null);

  const { data: assignedPosts, isLoading: loading } = useAssignedPosts(jobId);

  const userPosts = React.useMemo<UserPost[]>(() => {
    const grouped = groupBy(assignedPosts ?? [], 'assignedToId');
    return Object.keys(grouped).map((key) => ({
      name: grouped[key][0].assignedTo?.fullName ?? '',
      id: key,
      posts: grouped[key],
    }));
  }, [assignedPosts]);

  const selectedUser =
    userPosts.find((row) => row.id === selectedUserId) ?? null;

  const columns = React.useMemo<MRT_ColumnDef<UserPost>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Full Name',
      },
      {
        accessorKey: 'posts',
        header: 'Open Assignments',
        Cell: ({ row }) => row.original.posts.length,
      },
    ],
    [],
  );

  const tableState = useTableState({
    identifier: 'job-user-activity-table',
    columns,
  });

  const tableExports = useTableExports<UserPost>(
    'job-user-activity',
    (data) => {
      return data.map((item) => ({
        ['Job Name']: selectedJob?.name,
        ['Job Number']: selectedJob?.jobNumber,
        ['Full Name']: item.name,
        ['Open Assignments']: item.posts.length,
      }));
    },
  );

  const table = useTable({
    ...tableState,
    tableOptions: {
      data: userPosts,
      columns,
      enableRowActions: true,
      renderRowActions: ({ row }) => (
        <ViewActionIcon onClick={() => setSelectedUserId(row.original.id)} />
      ),
      mantineTableBodyRowProps: ({ row }) => ({
        style: {
          backgroundColor:
            selectedUser?.id === row.original.id
              ? 'var(--mantine-color-blue-0)'
              : undefined,
        },
      }),
    },
    totalCount: userPosts.length,
    isLoading: loading,
    isFetching: false,
    isError: false,
    ...tableExports,
  });

  const closeEditDialog = () => {
    setOpenPost(null);
  };

  const renderPosts = () => {
    if (!selectedUser) {
      return null;
    }
    return (
      <Stack>
        {selectedUser.posts.map((post) => {
          return (
            <PostItem
              key={post.id}
              item={post}
              openEditDialog={setOpenPost}
              jobId={jobId}
            />
          );
        })}
      </Stack>
    );
  };

  return (
    <>
      <EditPost
        post={openPost}
        handleClose={closeEditDialog}
        onPostSaved={closeEditDialog}
        job={openPost?.job as unknown as JobDto | undefined}
      />
      <Grid>
        <Grid.Col span={{ base: 12, md: 4 }}>{table.table}</Grid.Col>
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Paper withBorder p="md">
            {selectedUser ? (
              <>
                <Title order={4} mb="md">
                  {selectedUser.name} - Unresolved Items
                </Title>
                {!loading && renderPosts()}
                {loading && <Loader size={24} color="blue" />}
              </>
            ) : (
              <Text ta="center">Select a user in the left panel</Text>
            )}
          </Paper>
        </Grid.Col>
      </Grid>
    </>
  );
};
